/**
 * Web Push helpers — service worker registration, subscribe, unsubscribe.
 * All functions are safe to call from any environment — they degrade gracefully
 * when the browser doesn't support Push.
 */
import { api } from "@/lib/api";

const SW_URL = "/service-worker.js";

/* eslint-disable no-undef */

export const pushSupported = () =>
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager"   in window &&
    "Notification"  in window;

export const notificationPermission = () =>
    typeof Notification !== "undefined" ? Notification.permission : "default";

/** base64url → Uint8Array (Web Push wants a raw byte array for the app key). */
function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const raw = atob(base64);
    const out = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
    return out;
}

/** Registers /service-worker.js once. Returns the registration. */
export async function ensureServiceWorker() {
    if (!pushSupported()) throw new Error("Браузер не підтримує Web Push");
    const existing = await navigator.serviceWorker.getRegistration(SW_URL);
    if (existing) return existing;
    return navigator.serviceWorker.register(SW_URL);
}

/** Asks the user for notification permission. Returns final state. */
export async function requestPermission() {
    if (!pushSupported()) return "denied";
    if (Notification.permission !== "default") return Notification.permission;
    return await Notification.requestPermission();
}

/**
 * Full subscribe flow:
 *   1. ensureServiceWorker
 *   2. request permission (if needed)
 *   3. fetch VAPID public key
 *   4. pushManager.subscribe(...)
 *   5. POST /push/subscribe
 *
 * Returns the subscription endpoint on success.
 * Throws Error with a user-friendly message on failure.
 */
export async function subscribeToPush() {
    if (!pushSupported()) throw new Error("Браузер не підтримує Web Push");

    const perm = await requestPermission();
    if (perm === "denied")  throw new Error("Сповіщення заблоковано в налаштуваннях браузера");
    if (perm !== "granted") throw new Error("Дозвіл не надано");

    const reg = await ensureServiceWorker();

    // Reuse existing subscription if present
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
        const { data } = await api.get("/push/vapid-key");
        if (!data?.public_key) throw new Error("Сервер ще не налаштовано для Push");
        sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(data.public_key),
        });
    }

    await api.post("/push/subscribe", sub.toJSON());
    return sub.endpoint;
}

/** Unsubscribes from push and tells the backend. */
export async function unsubscribeFromPush() {
    if (!pushSupported()) return false;
    const reg = await navigator.serviceWorker.getRegistration(SW_URL);
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
        try { await sub.unsubscribe(); } catch { /* ignore */ }
        try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch { /* ignore */ }
    } else {
        // No browser-level sub but backend may still hold one → wipe all for this user
        try { await api.post("/push/unsubscribe", {}); } catch { /* ignore */ }
    }
    return true;
}
